import L from "leaflet";

// World Imagery advertises levels 0–23, but fine imagery coverage varies per
// tile. blankTile=false makes missing imagery return 404 instead of a watermark.
export const IMAGERY_MAX_ZOOM = 23;
export const isArcGisTileUrl = (url) => /\/MapServer\/tile\/\{z\}\/\{y\}\/\{x\}/i.test(url);

export function imageryTileUrl(template, coords) {
  const url = L.Util.template(template, coords);
  return `${url}${url.includes("?") ? "&" : "?"}blankTile=false`;
}

export function parentTileCrop(coords, level, size = 256) {
  const scale = 2 ** (coords.z - level);
  const x = Math.floor(coords.x / scale), y = Math.floor(coords.y / scale);
  return { x, y, z: level, width: size * scale,
    left: -(coords.x - x * scale) * size, top: -(coords.y - y * scale) * size };
}

export const AdaptiveImageryLayer = L.GridLayer.extend({
  initialize(url, options) {
    L.setOptions(this, options);
    this._imageryUrl = url;
    this._imageryCache = new Map();
    this.on("tileunload", ({ tile }) => tile.cancelImagery?.());
  },
  createTile(coords, done) {
    const tile = document.createElement("div");
    tile.style.overflow = "hidden";
    const img = document.createElement("img");
    img.alt = "";
    img.setAttribute("role", "presentation");
    img.draggable = false;
    Object.assign(img.style, { position: "absolute", maxWidth: "none", maxHeight: "none" });
    tile.appendChild(img);
    let level = coords.z, cancelled = false, completed = false, notified = false;
    let preview = null, previewReady = null;
    const remember = (url, available) => {
      this._imageryCache.delete(url);
      this._imageryCache.set(url, { available, time: Date.now() });
      if (this._imageryCache.size > 512) this._imageryCache.delete(this._imageryCache.keys().next().value);
    };
    const cached = (url) => {
      const entry = this._imageryCache.get(url);
      // A failed request can be temporary. Retry it later, not on every wheel tick.
      return entry && (entry.available || Date.now() - entry.time < 30000) ? entry : null;
    };
    const applyCrop = (image, crop) => Object.assign(image.style, {
      width: `${crop.width}px`, height: `${crop.width}px`, left: `${crop.left}px`, top: `${crop.top}px`,
    });
    // Already loaded ancestors provide immediate, geographically correct imagery
    // while the native tile is refined. No change to the map or drawing scale.
    for (let ancestor = coords.z; ancestor >= 0; ancestor -= 1) {
      const crop = parentTileCrop(coords, ancestor, this.getTileSize().x);
      const source = imageryTileUrl(this._imageryUrl, crop);
      if (!cached(source)?.available) continue;
      preview = img.cloneNode(false);
      applyCrop(preview, crop);
      preview.src = source;
      tile.appendChild(preview);
      break;
    }
    // GridLayer registers the tile after createTile returns.
    if (preview) previewReady = setTimeout(() => {
      if (!cancelled && !notified) { notified = true; done(null, tile); }
    }, 0);
    const finish = (error) => {
      if (cancelled || completed) return;
      completed = true;
      img.onload = img.onerror = null;
      if (!notified) { notified = true; done(preview ? null : error, tile); }
    };
    const load = () => {
      let crop, source;
      do {
        crop = parentTileCrop(coords, level, this.getTileSize().x);
        source = imageryTileUrl(this._imageryUrl, crop);
        if (cached(source)?.available !== false) break;
        level -= 1;
      } while (level >= 0);
      if (level < 0) { finish(new Error("Imagery is unavailable")); return; }
      img.style.visibility = "hidden";
      applyCrop(img, crop);
      img.src = source;
    };
    img.onload = () => {
      if (cancelled || completed) return;
      remember(img.src, true);
      img.style.visibility = "visible";
      preview?.remove();
      finish(null);
    };
    img.onerror = () => {
      if (cancelled || completed) return;
      remember(img.src, false);
      if (level > 0) { level -= 1; load(); }
      else finish(new Error("Imagery is unavailable"));
    };
    tile.cancelImagery = () => {
      cancelled = true;
      clearTimeout(previewReady);
      img.onload = img.onerror = null;
      img.removeAttribute("src");
    };
    load();
    return tile;
  },
});
